import { useState } from 'react';

import { useCancelTaxDocument, type TaxDocument } from '@/api/taxDocuments';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { Button, Notice } from '@/components/PageHeading';
import { useEscapeKey } from '@/components/shortcuts';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDocumentDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { canCancel, documentTitle, lineNote, orderedLines, taxColumns } from './register';

/**
 * One statutory document, read back exactly as it was filed.
 *
 * <p>
 * <strong>Nothing here is editable and there is no form.</strong> An issued tax document is never
 * corrected — every change moves through a credit or debit note — so the only action on the panel is
 * the withdrawal of one raised in error, and even that is refused by the server once anything has
 * actually been supplied under it.
 * </p>
 *
 * <p>
 * Hand-rolled rather than a `FormDrawer` for that reason, and it takes the same overlay tokens every
 * floating surface in this product takes: a raised surface is lighter than its ground in both
 * themes, and the scrim is much stronger in dark.
 * </p>
 */
export function TaxDocumentDrawer({
  document,
  onClose,
  onPrint,
}: {
  document: TaxDocument;
  onClose: () => void;
  /** Hands the sheet to the register, which owns `PrintFrame` — see the page for why. */
  onPrint: () => void;
}) {
  const [reason, setReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const cancel = useCancelTaxDocument();

  useEscapeKey(onClose, true);

  const columns = taxColumns(document);
  const lines = orderedLines(document.lines);

  return (
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={`${documentTitle(document)} ${document.documentNumber}`}
    >
      <div className="absolute inset-0 bg-[var(--overlay-scrim)]" aria-hidden="true" />

      <div className="relative flex h-full w-[640px] flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg max-sm:w-full">
        <div className="flex items-start justify-between gap-4 border-b border-border-color p-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="mb-0 text-base font-bold text-gray-900">{documentTitle(document)}</h2>

              {document.status === 'Cancelled' && (
                <StatusBadge tone="neutral">Cancelled</StatusBadge>
              )}
            </div>

            <p className="font-mono-nums mt-0.5 text-[13px] text-default">
              {document.documentNumber} · {formatDocumentDate(document.documentDate)}
              {document.bookingNumber ? ` · ${document.bookingNumber}` : ''}
            </p>
          </div>

          <button
            type="button"
            className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border-color hover:bg-light"
            aria-label="Close"
            onClick={onClose}
          >
            <i className="icon icon-x text-sm" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {document.againstDocumentNumber && (
            <div className="mb-4">
              <Notice tone="info" title={`Against invoice ${document.againstDocumentNumber}`}>
                {/* The link a GSTR-1 credit/debit note table needs, and the reason a note with no
                    parent is not a document at all. */}
                {document.reason ?? 'Raised against the invoice named above.'}
              </Notice>
            </div>
          )}

          {document.isBillOfSupply && (
            <div className="mb-4">
              <Notice tone="info" title="Bill of supply">
                No GSTIN was on this branch when the document was raised, so no tax was charged.
              </Notice>
            </div>
          )}

          {/* Both parties as the document froze them — never a join resolved today, or a customer
              who moved state would rewrite what was filed. */}
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <Party
              heading="From"
              name={document.supplierName}
              gstin={document.supplierGstin}
              stateCode={document.supplierStateCode}
              address={document.supplierAddress}
            />

            <Party
              heading="To"
              name={document.customerName}
              gstin={document.customerGstin}
              stateCode={document.customerStateCode}
              address={document.customerAddress}
              extra={document.customerMobile}
            />
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-light px-3 py-2 text-[13px] text-default">
            <span>
              Place of supply:{' '}
              <span className="font-mono-nums text-dark">{document.placeOfSupply ?? '—'}</span>
            </span>
            <span>
              {document.isInterState ? 'Inter-state (IGST)' : 'Intra-state (CGST + SGST)'}
            </span>
          </div>

          <div className="mb-4 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border-color text-left text-default">
                  <th className="py-2 pr-3 font-medium">Description</th>
                  <th className="py-2 pr-3 font-medium">SAC</th>
                  <th className="py-2 pr-3 text-right font-medium">Taxable</th>
                  <th className="py-2 pr-3 text-right font-medium">Rate</th>
                  <th className="py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>

              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-b border-border-color align-top">
                    <td className="py-2 pr-3">
                      <div className="text-dark">{line.description}</div>

                      {/* The sub-label is dropped when it only repeats the description, which is
                          what a late-fee line does — its description *is* "Late return", and the
                          word printed twice under itself reads as a rendering fault. */}
                      {lineNote(line) && (
                        <div className="text-[11px] text-default">{lineNote(line)}</div>
                      )}
                    </td>
                    <td className="font-mono-nums py-2 pr-3">{line.sacCode ?? '—'}</td>
                    <td className="font-mono-nums py-2 pr-3 text-right">
                      {formatAmount(line.taxableValue)}
                    </td>
                    <td className="font-mono-nums py-2 pr-3 text-right">{line.ratePct}%</td>
                    <td className="font-mono-nums py-2 text-right">
                      {formatAmount(line.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* The rate-wise summary a GST invoice has to carry. Grouped from the lines above by the
              server rather than stored, so it cannot disagree with them. */}
          {columns !== 'none' && document.rateSummary.length > 0 && (
            <div className="mb-4">
              <div className="mb-2 text-[13px] font-semibold text-gray-900">Tax summary</div>

              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-border-color text-left text-default">
                      <th className="py-2 pr-3 font-medium">Rate</th>
                      <th className="py-2 pr-3 text-right font-medium">Taxable</th>
                      {columns === 'igst' ? (
                        <th className="py-2 text-right font-medium">IGST</th>
                      ) : (
                        <>
                          <th className="py-2 pr-3 text-right font-medium">CGST</th>
                          <th className="py-2 text-right font-medium">SGST</th>
                        </>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {document.rateSummary.map((row) => (
                      <tr key={row.ratePct} className="border-b border-border-color">
                        <td className="font-mono-nums py-2 pr-3">{row.ratePct}%</td>
                        <td className="font-mono-nums py-2 pr-3 text-right">
                          {formatAmount(row.taxableValue)}
                        </td>
                        {columns === 'igst' ? (
                          <td className="font-mono-nums py-2 text-right">
                            {formatAmount(row.igstAmount)}
                          </td>
                        ) : (
                          <>
                            <td className="font-mono-nums py-2 pr-3 text-right">
                              {formatAmount(row.cgstAmount)}
                            </td>
                            <td className="font-mono-nums py-2 text-right">
                              {formatAmount(row.sgstAmount)}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="rounded-md border border-border-color">
            <Total label="Taxable value" value={document.taxableValue} />

            {columns === 'cgst-sgst' && (
              <>
                <Total label="CGST" value={document.cgstAmount} />
                <Total label="SGST" value={document.sgstAmount} />
              </>
            )}

            {columns === 'igst' && <Total label="IGST" value={document.igstAmount} />}

            {/* A line rather than a silent adjustment: a total that does not equal the sum above
                it, with nothing explaining the difference, is the first thing an accountant asks
                about. */}
            {document.roundOff !== 0 && <Total label="Round off" value={document.roundOff} />}

            <div className="flex items-center justify-between gap-4 px-3 py-2.5">
              <span className="text-sm font-semibold text-gray-900">Total</span>
              <span className="font-mono-nums text-sm font-semibold text-gray-900">
                {formatAmount(document.documentTotal)}
              </span>
            </div>
          </div>

          {document.status === 'Cancelled' && document.reason && (
            <p className="mt-3 text-[13px] text-default">{document.reason}</p>
          )}
        </div>

        <div className="border-t border-border-color bg-light p-4">
          {cancel.isError && (
            <div className="mb-3">
              <ApiErrorAlert error={cancel.error} />
            </div>
          )}

          {withdrawing ? (
            <div className="flex flex-col gap-2">
              <label className="text-[13px] text-default" htmlFor="tax-cancel-reason">
                Why is this being withdrawn? The number stays in the sequence either way.
              </label>

              <input
                id="tax-cancel-reason"
                className="w-full rounded-md border border-border-color bg-white px-3 py-2 text-sm"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                autoFocus
              />

              <div className="flex items-center justify-end gap-2">
                <Button onClick={() => setWithdrawing(false)}>Keep it</Button>
                <Button
                  variant="primary"
                  loading={cancel.isPending}
                  disabled={reason.trim().length === 0 || cancel.isPending}
                  onClick={() =>
                    cancel.mutate(
                      { id: document.id, reason: reason.trim() },
                      { onSuccess: onClose },
                    )
                  }
                >
                  {cancel.isPending ? 'Cancelling…' : 'Cancel document'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-default">
                An issued document is never edited. A correction is a credit or debit note.
              </span>

              <div className="flex shrink-0 items-center gap-2">
                {/* Alt+P belongs to this panel while it is open — `data-shortcut-layer` on the root
                    stops the key reaching the register behind, which would otherwise print the page
                    the person cannot see. */}
                <Button
                  variant="primary"
                  icon="icon icon-printer"
                  shortcut="print"
                  onClick={onPrint}
                >
                  Print
                </Button>

                {canCancel(document) && (
                  <Button onClick={() => setWithdrawing(true)}>Cancel document</Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Party({
  heading,
  name,
  gstin,
  stateCode,
  address,
  extra,
}: {
  heading: string;
  name: string;
  gstin: string | null;
  stateCode: string | null;
  address: string | null;
  extra?: string | null;
}) {
  return (
    <div className="rounded-md border border-border-color p-3">
      <div className="text-[11px] tracking-wide text-default uppercase">{heading}</div>
      <div className="mt-1 text-sm font-semibold text-gray-900">{name}</div>

      {address && <div className="mt-0.5 text-[13px] text-default">{address}</div>}
      {extra && <div className="font-mono-nums mt-0.5 text-[13px] text-default">{extra}</div>}

      <div className="font-mono-nums mt-1 text-[13px] text-default">
        GSTIN {gstin ?? '—'}
        {stateCode ? ` · State ${stateCode}` : ''}
      </div>
    </div>
  );
}

function Total({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border-color px-3 py-2 text-[13px]">
      <span className="text-default">{label}</span>
      <span className="font-mono-nums text-dark">{formatAmount(value)}</span>
    </div>
  );
}
